import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormCaptureFields } from '@/components/vsleads/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SliderNumericField } from '@/components/ui/numeric-input';
import { ShadowPicker } from '@/components/ui/shadow-picker';

interface StyleTabProps {
  form: UseFormReturn<FormCaptureFields>;
}

export function StyleTab({ form }: StyleTabProps) {
  useLanguage();
  const style = form.watch('style');

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Container do Formulário")}</CardTitle>
          <CardDescription>{translateInterfaceText("Aparência geral do formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Cor de Fundo")}</Label>
            <Input
              type="color"
              value={style.container.background.value}
              onChange={(e) =>
                form.setValue('style.container.background.value', e.target.value, { shouldDirty: true })
              }
              className="w-full h-10"
            />
          </div>

          <SliderNumericField
            label={translateInterfaceText("Opacidade")}
            value={style.container.background.opacity}
            onChange={(value) =>
              form.setValue('style.container.background.opacity', value, { shouldDirty: true })
            }
            min={0.6}
            max={1}
            step={0.05}
          />

          <SliderNumericField
            label={translateInterfaceText("Raio de Borda (Arredondamento)")}
            value={style.container.radius}
            onChange={(value) =>
              form.setValue('style.container.radius', value, { shouldDirty: true })
            }
            min={0}
            max={24}
            step={2}
            suffix="px"
          />

          <ShadowPicker
            value={style.container.shadow}
            onChange={(value) =>
              form.setValue('style.container.shadow', value, { shouldDirty: true })
            }
          />

          {style.container.background.opacity < 1 && (
            <SliderNumericField
              label={translateInterfaceText("Blur de Fundo (Glass Effect)")}
              value={style.container.blur}
              onChange={(value) =>
                form.setValue('style.container.blur', value, { shouldDirty: true })
              }
              min={0}
              max={12}
              step={2}
              suffix="px"
            />
          )}

          <div>
            <SliderNumericField
              label={translateInterfaceText("Espessura da Borda")}
              value={style.container.border.width}
              onChange={(value) =>
                form.setValue('style.container.border.width', value, { shouldDirty: true })
              }
              min={0}
              max={4}
              step={1}
              suffix="px"
            />
            {style.container.border.width > 0 && (
              <div className="mt-3">
                <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Cor da Borda")}</Label>
                <Input
                  type="color"
                  value={style.container.border.color}
                  onChange={(e) =>
                    form.setValue('style.container.border.color', e.target.value, { shouldDirty: true })
                  }
                  className="w-full h-10"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Campos de Entrada")}</CardTitle>
          <CardDescription>{translateInterfaceText("Estilo dos inputs do formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Formato")}</Label>
            <RadioGroup
              value={style.inputs.shape}
              onValueChange={(value: 'rounded' | 'square') =>
                form.setValue('style.inputs.shape', value, { shouldDirty: true })
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="rounded" id="rounded" />
                <Label htmlFor="rounded">{translateInterfaceText("Arredondado")}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="square" id="square" />
                <Label htmlFor="square">{translateInterfaceText("Retangular")}</Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Cor de Fundo")}</Label>
            <Input
              type="color"
              value={style.inputs.background}
              onChange={(e) =>
                form.setValue('style.inputs.background', e.target.value, { shouldDirty: true })
              }
              className="w-full h-10"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Cor da Borda")}</Label>
            <Input
              type="color"
              value={style.inputs.border}
              onChange={(e) =>
                form.setValue('style.inputs.border', e.target.value, { shouldDirty: true })
              }
              className="w-full h-10"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Cores do Texto")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Cor do Título")}</Label>
            <Input type="color" value={style.text.title} onChange={(e) => form.setValue('style.text.title', e.target.value, { shouldDirty: true })} className="w-full h-10" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">{translateInterfaceText("Cor do Corpo")}</Label>
            <Input type="color" value={style.text.body} onChange={(e) => form.setValue('style.text.body', e.target.value, { shouldDirty: true })} className="w-full h-10" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Botão")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{translateInterfaceText("Cor de Fundo")}</Label>
            <Input type="color" value={style.button.background} onChange={(e) => form.setValue('style.button.background', e.target.value, { shouldDirty: true })} className="w-full h-10" />
          </div>
          <div>
            <Label>{translateInterfaceText("Cor do Texto")}</Label>
            <Input type="color" value={style.button.text} onChange={(e) => form.setValue('style.button.text', e.target.value, { shouldDirty: true })} className="w-full h-10" />
          </div>
          <div>
            <Label>{translateInterfaceText("Hover")}</Label>
            <Input type="color" value={style.button.hoverBackground} onChange={(e) => form.setValue('style.button.hoverBackground', e.target.value, { shouldDirty: true })} className="w-full h-10" />
          </div>
          <div>
            <Label>{translateInterfaceText("Pressionado")}</Label>
            <Input type="color" value={style.button.pressedBackground} onChange={(e) => form.setValue('style.button.pressedBackground', e.target.value, { shouldDirty: true })} className="w-full h-10" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-base">{translateInterfaceText("Overlay de Vídeo")}</CardTitle>
        </CardHeader>
        <CardContent>
          <SliderNumericField label={translateInterfaceText("Escurecimento")} value={style.overlayOnShow} onChange={(value) => form.setValue('style.overlayOnShow', value, { shouldDirty: true })} min={0} max={0.6} step={0.1} />
        </CardContent>
      </Card>
    </div>
  );
}
